import { useState } from "react";
import { useI18n } from "../i18n/LanguageContext";

// On-brand bronze tones (no rainbow) — depth via decreasing opacity.
const BAR_COLORS = [
  "var(--br-bronze)",
  "rgba(167,128,73,0.78)",
  "rgba(167,128,73,0.58)",
  "rgba(167,128,73,0.42)",
  "rgba(167,128,73,0.30)",
  "rgba(167,128,73,0.20)",
];

/**
 * Published target asset allocation for a plan/tier, with the volatility band
 * and a methodological note. Reused on the Plans page and the yield calculator.
 * When `collapsible`, the breakdown is hidden behind a "View asset allocation"
 * button (client-facing UX).
 */
export default function AssetAllocation({
  allocation = [],
  volatilityTarget,
  note = true,
  collapsible = false,
  className = "",
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(!collapsible);
  if (!allocation.length) return null;

  const body = (
    <div data-testid="allocation-content">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div
          className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase"
          style={{ color: "var(--br-bronze)" }}
        >
          {t("plans.allocation_title")}
        </div>
        {volatilityTarget ? (
          <div
            className="text-[0.7rem] text-[color:var(--br-text-dim)]"
            data-testid="allocation-volatility"
          >
            {t("plans.volatility_target")}:{" "}
            <span className="text-[color:var(--br-parchment)] font-mono-fin">
              {volatilityTarget}
            </span>
          </div>
        ) : null}
      </div>
      <div className="space-y-3">
        {allocation.map((s, i) => (
          <div key={`${s.key}-${i}`} data-testid={`alloc-slice-${i}`}>
            <div className="flex items-center justify-between text-[0.8rem] mb-1">
              <span className="text-[color:var(--br-text-dim)]">{t(s.key)}</span>
              <span className="text-[color:var(--br-parchment)] font-mono-fin">
                {s.percentage}%
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: "var(--br-line)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${s.percentage}%`,
                  background: BAR_COLORS[i % BAR_COLORS.length],
                  transition: "width 0.5s ease",
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {note ? (
        <p
          className="mt-5 text-[0.7rem] leading-relaxed italic"
          style={{ color: "var(--br-text-dim)" }}
          data-testid="allocation-note"
        >
          {t("plans.allocation_note")}
        </p>
      ) : null}
    </div>
  );

  if (!collapsible) {
    return (
      <div className={className} data-testid="asset-allocation">
        {body}
      </div>
    );
  }

  return (
    <div className={className} data-testid="asset-allocation">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="br-btn-outline"
        aria-expanded={open}
        data-testid="toggle-allocation-btn"
      >
        {open ? t("plans.allocation_hide") : t("plans.allocation_show")}
      </button>
      {open ? <div className="mt-5">{body}</div> : null}
    </div>
  );
}
